import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  supportCaseCategory,
  supportCaseStatus,
  supportPriority,
  type SupportOperationsQuery,
} from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  Select,
  SkeletonList,
  Stack,
} from '@dental-staffing-platform/ui';
import { supportOperationsApi } from '../lib/support-operations-client.js';
import { useAsync } from '../lib/useAsync.js';
import { humanize } from '../lib/format.js';

export { SupportCaseDetail } from './SupportCaseDetail.js';

export function SupportCases() {
  const id = useId();
  const [search, setSearch] = useState(''),
    [status, setStatus] = useState(''),
    [category, setCategory] = useState(''),
    [priority, setPriority] = useState(''),
    [assignment, setAssignment] = useState('all');
  const [filter, setFilter] = useState<Partial<SupportOperationsQuery>>({
      pageSize: 20,
      assignment: 'all',
    }),
    [page, setPage] = useState(1);
  const state = useAsync(() => supportOperationsApi.list({ ...filter, page }), [filter, page]);
  const apply = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setFilter({
      pageSize: 20,
      search,
      ...(supportCaseStatus.safeParse(status).success
        ? { status: supportCaseStatus.parse(status) }
        : {}),
      ...(supportCaseCategory.safeParse(category).success
        ? { category: supportCaseCategory.parse(category) }
        : {}),
      ...(supportPriority.safeParse(priority).success
        ? { priority: supportPriority.parse(priority) }
        : {}),
      assignment:
        assignment === 'mine' ? 'mine' : assignment === 'unassigned' ? 'unassigned' : 'all',
    });
  };
  return (
    <Stack gap={20}>
      <PageHeader
        title="Support queue"
        subtitle="Triage booking issues, own the follow-up and keep private notes separate from shared replies."
        actions={<Button onClick={state.reload}>Refresh queue</Button>}
      />
      <p className="dsp-muted">
        Priority is an internal work order. It does not promise a response deadline or emergency
        coverage. Case decisions do not move money or change approved work.
      </p>
      <Card>
        <form onSubmit={apply}>
          <Row gap={12} style={{ flexWrap: 'wrap', alignItems: 'end' }}>
            <Field label="Search support cases" htmlFor={`${id}-search`}>
              <Input
                id={`${id}-search`}
                type="search"
                value={search}
                maxLength={100}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Field>
            <Field label="Case status" htmlFor={`${id}-status`}>
              <Select
                id={`${id}-status`}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All statuses</option>
                {supportCaseStatus.options.map((value) => (
                  <option key={value} value={value}>
                    {humanize(value)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Case category" htmlFor={`${id}-category`}>
              <Select
                id={`${id}-category`}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">All categories</option>
                {supportCaseCategory.options.map((value) => (
                  <option key={value} value={value}>
                    {humanize(value)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Priority" htmlFor={`${id}-priority`}>
              <Select
                id={`${id}-priority`}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                <option value="">All priorities</option>
                {supportPriority.options.map((value) => (
                  <option key={value} value={value}>
                    {humanize(value)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Assignment" htmlFor={`${id}-assignment`}>
              <Select
                id={`${id}-assignment`}
                value={assignment}
                onChange={(e) => setAssignment(e.target.value)}
              >
                <option value="all">All operators</option>
                <option value="mine">Assigned to me</option>
                <option value="unassigned">Unassigned</option>
              </Select>
            </Field>
            <Button type="submit">Apply queue filters</Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setSearch('');
                setStatus('');
                setCategory('');
                setPriority('');
                setAssignment('all');
                setPage(1);
                setFilter({ pageSize: 20, assignment: 'all' });
              }}
            >
              Clear filters
            </Button>
          </Row>
        </form>
      </Card>
      {state.loading ? (
        <SkeletonList count={3} />
      ) : state.error ? (
        <div role="alert">
          <p>{state.error}</p>
          <Button onClick={state.reload}>Retry queue</Button>
        </div>
      ) : !state.data?.items.length ? (
        <EmptyState
          title="No matching support cases"
          description="Booking parties can open cases from their workspace. Change the filters or refresh the queue."
        />
      ) : (
        <>
          {state.data.items.map((item) => (
            <Card key={item.id}>
              <Stack gap={12}>
                <Row style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <div>
                    <h2 style={{ fontSize: '1.15rem', overflowWrap: 'anywhere' }}>
                      {item.subject}
                    </h2>
                    <p className="dsp-muted">
                      {item.reference} · {humanize(item.category)}
                    </p>
                  </div>
                  <Row>
                    <Badge tone={item.status === 'closed' ? 'neutral' : 'info'}>
                      {humanize(item.status)}
                    </Badge>
                    <Badge
                      tone={
                        item.priority === 'urgent' || item.priority === 'high'
                          ? 'warning'
                          : 'neutral'
                      }
                    >
                      {humanize(item.priority)}
                    </Badge>
                  </Row>
                </Row>
                <p>
                  {item.locationName} · {item.professionalName}
                </p>
                <p>Owner: {item.assignedName ?? 'Unassigned'}</p>
                <p className="dsp-muted">
                  Shared update: {new Date(item.updatedAt).toLocaleString()}
                  {item.operationsUpdatedAt
                    ? ` · Operator update: ${new Date(item.operationsUpdatedAt).toLocaleString()}`
                    : ''}
                </p>
                <Link
                  className="dsp-btn dsp-btn--secondary dsp-btn--md"
                  to={`/support/${encodeURIComponent(item.id)}`}
                >
                  Open case
                </Link>
              </Stack>
            </Card>
          ))}
          <Pagination
            page={state.data.page}
            pageSize={state.data.pageSize}
            total={state.data.total}
            totalPages={state.data.totalPages}
            onPageChange={setPage}
            itemLabel="support cases"
          />
        </>
      )}
    </Stack>
  );
}
